import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { UserProcessHttpService } from '@core/http/user-process.http.service';
import { SaveUserDocumentPayload, UserProcessStatus } from '@interfaces';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { parseError } from '@state/errors.parser';
import { TranslateService } from '@ngx-translate/core';
import { FileHandlerService } from '@core/services/file-handler.service';
import * as routerActions from '@state/router/router.actions';
import * as userProcessActions from './user-process.actions';

@Injectable()
export class UserProcessEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(UserProcessHttpService);
  private readonly notifService = inject(StackedMessagesService);
  private readonly translateService = inject(TranslateService);
  private readonly fileHandlerService = inject(FileHandlerService);

  getMyProcesses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.getMyProcesses),
      switchMap(() => {
        return this.http.getMyProcesses().pipe(
          map(myProcesses => {
            return userProcessActions.getMyProcessesSuccess({ myProcesses });
          }),
          catchError(error => {
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.getMyProcesses.type);
            return of(userProcessActions.getMyProcessesError({ errorMessage }));
          })
        );
      })
    )
  );

  getMyProcessDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.getMyProcessDetails),
      switchMap(({ userProcessId }) => {
        return this.http.getMyProcessDetails(userProcessId).pipe(
          map(myProcess => userProcessActions.getMyProcessDetailsSuccess({ myProcess })),
          catchError(error => {
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.getMyProcessDetails.type);
            return of(
              routerActions.changeRoute({ linkParams: ['/'] }),
              userProcessActions.getMyProcessDetailsError({ errorMessage })
            );
          })
        );
      })
    )
  );

  addNewUserProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.addNewUserProcess),
      switchMap(({ payload }) => {
        return this.http.addNewUserProcess(payload).pipe(
          map(addedUserProcess => userProcessActions.addNewUserProcessSuccess({ addedUserProcess })),
          catchError(error => {
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.addNewUserProcess.type);
            return of(userProcessActions.addNewUserProcessError({ errorMessage }));
          })
        );
      })
    )
  );

  pickProcess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.pickProcess),
      switchMap(({ userProcessId, processId }) => {
        return this.http.pickProcess(userProcessId, processId).pipe(
          map(userProcess => {
            return userProcessActions.pickProcessSuccess({ userProcess });
          }),
          catchError(error => {
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.pickProcess.type);
            return of(userProcessActions.pickProcessError({ errorMessage }));
          })
        );
      })
    )
  );

  updatePersonalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.updatePersonalDetails),
      switchMap(({ personalDetails, userProcessId }) => {
        return this.http.updatePersonalDetails(userProcessId, personalDetails).pipe(
          map(updatedUserProcess =>
            userProcessActions.updatePersonalDetailsSuccess({ updatedUserProcess, userProcessId })
          ),
          catchError(error => {
            console.error(error);
            // We don't have personal-details loading/error indicator anymore
            // So if update errors -> showing stacked message
            this.notifService.showError({ message: 'COMMON.ERROR.ERROR_UPDATING_PERSONAL_DETAILS' });
            const errorMessage = parseError(error, userProcessActions.updatePersonalDetails.type);
            return of(userProcessActions.updatePersonalDetailsError({ errorMessage }));
          })
        );
      })
    )
  );

  updatePreSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.updatePreSurvey),
      switchMap(({ opts }) => {
        return this.http.updatePreSurvey(opts).pipe(
          map(preSurvey =>
            userProcessActions.updatePreSurveySuccess({
              preSurvey,
              userId: opts.userId,
              userProcessId: opts.userProcessId,
            })
          ),
          catchError(error => {
            console.error(error);
            return of(userProcessActions.updatePreSurveyError());
          })
        );
      })
    )
  );

  sendPreSurveyForVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.sendPreSurveyForVerification),
      switchMap(({ opts }) => {
        return this.http.sendPreSurveyForVerification(opts).pipe(
          map(userProcess => userProcessActions.sendPreSurveyForVerificationSuccess({ userProcess })),
          catchError(error => {
            console.error(error);
            return of(userProcessActions.sendPreSurveyForVerificationError());
          })
        );
      })
    )
  );

  confirmDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.confirmPersonalDetails),
      switchMap(({ userProcessId }) => {
        return this.http.confirmDetails(userProcessId).pipe(
          map(validationResult =>
            userProcessActions.confirmPersonalDetailsSuccess({ userProcessId, validationResult })
          ),
          catchError(error => {
            console.error(error);
            return of(
              userProcessActions.confirmPersonalDetailsError({
                errorMessage: 'ERROR_CONFIRMING_PERSONAL_DETAILS_IN_USER_PROCESS',
              })
            );
          })
        );
      })
    )
  );

  startCompletingDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.startCompletingDocuments),
      switchMap(({ userProcessId }) => {
        return this.http.startCompletingDocuments(userProcessId).pipe(
          map(updatedUserProcess => userProcessActions.startCompletingDocumentsSuccess({ updatedUserProcess })),
          catchError(error => {
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.startCompletingDocuments.type);
            return of(userProcessActions.startCompletingDocumentsError({ errorMessage }));
          })
        );
      })
    )
  );

  startCompletingDocumentsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.startCompletingDocumentsSuccess),
      map(({ updatedUserProcess }) => {
        const { status, id: userProcessId } = updatedUserProcess;
        const linkParams = [`/user-process/${userProcessId}`];
        if (status !== UserProcessStatus.COMPLETING_DOCUMENTS) {
          this.notifService.showError({ message: 'UNKNOWN_ERROR_WHILE_UPDATING_PROCESS_FROM_DETAILS' });
          console.warn(
            'Something went wrong with assigning correct process&status. Returned userProcess: ',
            updatedUserProcess
          );
          linkParams.push(`user-details`);
        } else {
          linkParams.push(`documents`);
        }

        return routerActions.changeRoute({ linkParams });
      })
    )
  );

  sendAppToReview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.sendAppToReview),
      switchMap(({ userProcessId }) => {
        return this.http.sendApplicationToReview(userProcessId).pipe(
          switchMap(updatedUserProcess => [
            userProcessActions.sendAppToReviewSuccess({ updatedUserProcess }),
            userProcessActions.getDocumentsList({ userProcessId: updatedUserProcess.id }),
          ]),
          catchError(error => {
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.sendAppToReview.type);
            return of(userProcessActions.sendAppToReviewError({ errorMessage }));
          })
        );
      })
    )
  );

  getDocumentsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.getDocumentsList),
      switchMap(({ userProcessId }) => {
        return this.http.getDocumentsList(userProcessId).pipe(
          map(documentsList => {
            return userProcessActions.getDocumentsListSuccess({ documentsList });
          }),
          catchError(error => {
            console.error(error);
            this.notifService.showError({ message: 'MISSING_PROCESS_DOCUMENTS' });
            const errorMessage = parseError(error, userProcessActions.getDocumentsList.type);
            return of(userProcessActions.getDocumentsListError({ errorMessage }));
          })
        );
      })
    )
  );
  addFilesToMyDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.addFilesToMyDocument),
      switchMap(({ opts }) => {
        return this.http.addFilesToMyDocument(opts).pipe(
          map(userDocument => userProcessActions.addFilesToMyDocumentSuccess({ userDocument })),
          catchError(error => {
            console.error(`Error 'addFilesTOMyDocument'`);
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.addFilesToMyDocument.type);
            this.notifService.showError({ message: errorMessage });
            return of(userProcessActions.addFilesToMyDocumentError());
          })
        );
      })
    )
  );

  answerToMyDocQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.answerToMyDocQuestion),
      switchMap(({ opts }) => {
        return this.http.answerToMyDocQuestion(opts).pipe(
          map(userDocument => userProcessActions.answerToMyDocQuestionSuccess({ userDocument })),
          catchError(error => {
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.answerToMyDocQuestion.type);
            this.notifService.showError({ message: errorMessage });
            return of(userProcessActions.answerToMyDocQuestionError());
          })
        );
      })
    )
  );

  saveUserDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.saveUserDocument),
      switchMap(({ saveDocumentPayload, userDocId }) => {
        let resultStream;
        if (!userDocId) {
          resultStream = this.http.addDocument(saveDocumentPayload);
        }
        if (userDocId) {
          resultStream = this.http.updateDocument(userDocId, saveDocumentPayload);
        }

        return resultStream.pipe(
          switchMap(userDocument => {
            const actionsToDispatch = [userProcessActions.saveUserDocumentSuccess({ userDocument })];

            // if no userDocId - user created completely new document (new step)
            // he's allowed to do that only with addtional documents
            if (!userDocId) {
              actionsToDispatch.push(userProcessActions.createNewAdditionalDocumentSuccess({ userDocument }) as any);
            }

            return actionsToDispatch;
          }),
          catchError(error => {
            console.error(error);
            const errorMessage = parseError(error, userProcessActions.saveUserDocument.type);
            return of(userProcessActions.saveUserDocumentError({ errorMessage }));
          })
        );
      })
    )
  );

  downloadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.downloadFile),
      mergeMap(({ userProcessId, userDocumentId, filename, previewTargetKey }) => {
        return this.http.downloadFile(userProcessId, userDocumentId, filename).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return userProcessActions.downloadFileSuccess();
            } else {
              return userProcessActions.downloadFileError();
            }
          }),
          catchError(err => {
            console.error(`Error 'downloadFile'`);
            console.error(err);
            if (previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(previewTargetKey);
            }
            this.notifService.showError({ message: 'COMMON.ERROR.DOWNLOADING_ATTACHMENT' });
            return of(userProcessActions.downloadFileError());
          })
        );
      })
    )
  );

  removeFileOfUserDoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.removeFileOfUserDoc),
      switchMap(({ userProcessId, userDocumentId, filename }) => {
        return this.http.removeFileOfUserDoc(userProcessId, userDocumentId, filename).pipe(
          map(({ updatedUserDoc }) => {
            return userProcessActions.removeFileOfUserDocSuccess({ updatedUserDoc });
          }),
          catchError(error => {
            console.error(`Error 'removeFileOfUserDoc'`);
            console.error(error);
            // Show notifciation with error message
            this.notifService.showError({ message: 'COMMON.ERROR.UNABLE_TO_DELETE_FILE' });
            const errorMessage = parseError(error, userProcessActions.removeFileOfUserDoc.type);
            return of(userProcessActions.removeFileOfUserDocError({ errorMessage }));
          })
        );
      })
    )
  );

  removeUserDoc$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.removeUserDoc),
      switchMap(({ userProcessId, userDocumentId }) => {
        return this.http.removeDocument(userProcessId, userDocumentId).pipe(
          map(() => {
            return userProcessActions.removeUserDocSuccess({ removedUserDocId: userDocumentId });
          }),
          catchError(error => {
            console.error(`Error 'removeUserDoc'`);
            console.error(error);
            // Show notifciation with error message
            const errorMessage = parseError(error, userProcessActions.removeUserDoc.type);
            return of(userProcessActions.removeUserDocError({ errorMessage }));
          })
        );
      })
    )
  );

  saveUserAdditionalDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.saveAdditionalDocs),
      switchMap(({ additionalDocs }) => {
        return forkJoin(
          additionalDocs.files.map(additionalDoc => {
            let stream;
            const saveDocumentPayload: SaveUserDocumentPayload = {
              userProcessId: additionalDocs.userProcessId,
              documentId: additionalDocs.documentId,
              comments: additionalDoc.comments ? additionalDoc.comments : '',
              files: additionalDoc.files,
              willNotUploadDocument: false,
            };

            if (additionalDoc.userDocId) {
              stream = this.http.updateDocument(additionalDoc.userDocId, saveDocumentPayload);
            } else {
              stream = this.http.addDocument(saveDocumentPayload);
            }

            return stream;
          })
        ).pipe(
          map(savedUserDocuments => userProcessActions.saveAdditionalDocsSuccess({ savedUserDocuments })),
          catchError(error => {
            const errorMessage = parseError(error, userProcessActions.saveAdditionalDocs.type);
            return of(userProcessActions.saveAdditionalDocsError({ errorMessage }));
          })
        );
      })
    )
  );

  getDocumentsForSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.getDocumentsForSummary),
      switchMap(({ userProcessId }) => {
        return this.http.getDocumentsForSummary(userProcessId).pipe(
          map(summaryDocuments => userProcessActions.getDocumentsForSummarySuccess({ summaryDocuments })),
          catchError(error => {
            const errorMessage = parseError(error, userProcessActions.getDocumentsForSummary.type);
            return of(userProcessActions.getDocumentsForSummaryError({ errorMessage }));
          })
        );
      })
    )
  );

  downloadForPrintAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.downloadForPrintAll),
      mergeMap(({ userProcessId, previewTargetKey }) => {
        return this.http.downloadForPrintAll(userProcessId).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return userProcessActions.downloadForPrintAllSuccess();
            } else {
              return userProcessActions.downloadForPrintAllError();
            }
          }),
          catchError(() => {
            if (previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(previewTargetKey);
            }
            this.notifService.showError({ message: 'COMMON.ERROR.DOWNLOADING_ATTACHMENT' });
            return of(userProcessActions.downloadForPrintAllError());
          })
        );
      })
    )
  );

  downloadInstructions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.downloadInstructions),
      mergeMap(({ userProcessId, previewTargetKey }) => {
        return this.http.downloadInstructions(userProcessId).pipe(
          map(response => {
            if (
              this.fileHandlerService.getFile(response, {
                tryOpenOnMobile: true,
                previewTargetKey,
                allowDownloadFallback: true,
              })
            ) {
              return userProcessActions.downloadInstructionsSuccess();
            } else {
              return userProcessActions.downloadInstructionsError();
            }
          }),
          catchError(() => {
            if (previewTargetKey) {
              this.fileHandlerService.clearPreparedPreviewTarget(previewTargetKey);
            }
            this.notifService.showError({ message: 'COMMON.ERROR.INSTRUCTIONS_NOT_FOUND_LET_US_KNOW' });
            return of(userProcessActions.downloadInstructionsError());
          })
        );
      })
    )
  );

  saveMyDocumentsSigningMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.saveDocumentsSigningMode),
      switchMap(({ userProcessId, opts }) => {
        return this.http.saveDocumentsSigningMode(userProcessId, opts).pipe(
          map(userProcess => userProcessActions.saveDocumentsSigningModeSuccess({ userProcess })),
          catchError(error => {
            const errorMessage = parseError(error, userProcessActions.saveDocumentsSigningMode.type);
            return of(userProcessActions.saveDocumentsSigningModeError({ userProcessId, errorMessage }));
          })
        );
      })
    )
  );

  confirmVoivoChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.confirmVoivoChange),
      switchMap(({ userProcessId }) => {
        return this.http.confirmVoivoChange(userProcessId).pipe(
          map(() => userProcessActions.confirmVoivoChangeSuccess({ userProcessId })),
          catchError(error => {
            const errorMessage = parseError(error, userProcessActions.confirmVoivoChange.type);
            this.notifService.showWarning({ message: 'UNABLE_TO_CONFIRM_VOIVO_CHANGE' });
            return of(userProcessActions.confirmVoivoChangeError({ userProcessId, errorMessage }));
          })
        );
      })
    )
  );

  setAppointmentDateAndPlace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.setAppointmentDateAndPlace),
      switchMap(({ opts }) => {
        return this.http.setAppointmentDateAndPlace(opts).pipe(
          map(userProcess =>
            userProcessActions.setAppointmentDateAndPlaceSuccess({
              userProcess,
            })
          ),
          catchError(() => {
            const errorMssg = this.translateService.instant('APPOINTMENT_INSTRUCTIONS.SAVING_ERROR');
            this.notifService.showError({ message: errorMssg, duration: 4000, skipTranslation: true });
            return of(userProcessActions.setAppointmentDateAndPlaceError());
          })
        );
      })
    )
  );

  uploadDecision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.uploadDecision),
      switchMap(({ opts }) => {
        return this.http.uploadDecision(opts).pipe(
          map(userProcess => userProcessActions.uploadDecisionSuccess({ userProcess })),
          catchError(() => {
            const errorMssg = this.translateService.instant('IMMIGROF_VISIT_COMPLETED.SAVING_ERROR');
            this.notifService.showError({ message: errorMssg, duration: 4000, skipTranslation: true });
            return of(userProcessActions.uploadDecisionError());
          })
        );
      })
    )
  );

  confirmMOSDraftPreparation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.confirmMOSDraftPreparation),
      switchMap(({ userProcessId }) => {
        return this.http.confirmMOSDraftPreparation(userProcessId).pipe(
          map(userProcess => userProcessActions.confirmMOSDraftPreparationSuccess({ userProcess })),
          catchError(() => {
            const errorMssg = this.translateService.instant('MOS_PREPARATION_INSTRUCTIONS.CONFIRM_DRAFT_ERROR');
            this.notifService.showError({ message: errorMssg, duration: 4000, skipTranslation: true });
            return of(userProcessActions.confirmMOSDraftPreparationError({ errorMessage: errorMssg }));
          })
        );
      })
    )
  );

  confirmSubmissionOfApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.confirmSubmissionOfApplication),
      switchMap(({ userProcessId }) => {
        return this.http.confirmSubmissionOfApplication(userProcessId).pipe(
          map(userProcess => userProcessActions.confirmSubmissionOfApplicationSuccess({ userProcess })),
          catchError(() => {
            const errorMssg = this.translateService.instant('POST_SUBMISSION_INSTRUCTIONS.CONFIRM_SUBMISSION_ERROR');
            this.notifService.showError({ message: errorMssg, duration: 4000, skipTranslation: true });
            return of(userProcessActions.confirmSubmissionOfApplicationError());
          })
        );
      })
    )
  );

  confirmSendingByPost$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.confirmSendingByPost),
      switchMap(({ userProcessId }) => {
        return this.http.confirmSendingByPost(userProcessId).pipe(
          map(userProcess => userProcessActions.confirmSendingByPostSuccess({ userProcess })),
          catchError(() => {
            return of(userProcessActions.confirmSendingByPostError());
          })
        );
      })
    )
  );

  getPostAddressForSendingDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(userProcessActions.getPostAddressForSendingDocuments),
      switchMap(({ userProcessId }) => {
        return this.http.getPostAddressForSendingDocuments(userProcessId).pipe(
          map(address => userProcessActions.getPostAddressForSendingDocumentsSuccess({ address })),
          catchError(() => {
            return of(
              userProcessActions.getPostAddressForSendingDocumentsError({
                errorMessage: 'UNABLE_TO_FETCH_POST_ADDRESS_FOR_SENDING_DOCUMENTS',
              })
            );
          })
        );
      })
    )
  );
}
